import { LeftOutlined, LockOutlined, RightOutlined, UnlockOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { Card, Col, Form, Input, message, Modal, Row, Select, Tag, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { commissionApi } from "api/commission.api";
import { AppButton, AppTable } from "components/antd-ui";
import { StatusCell, TableContent, TableLayout, TableLayoutFooter } from "components/table";
import { useClientPaging } from "hooks/useClientPaging";
import { fetchStaffOptions, useRemoteOptions } from "hooks/useRemoteOptions";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { userStore } from "store/userStore";
import {
  commissionKpiResultTrans,
  CommissionMonthlyData,
  CommissionRevenueDetail,
  CommissionRow,
} from "types/commission";
import { getTitle } from "utils";
import { can } from "utils/auth";
import dayjs, { Dayjs } from "utils/dayjs";

const commissionPath = "/commission";
const { TextArea } = Input;

const formatVND = (num: number) => `${Math.round(num || 0).toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.")}đ`;

const EMPTY_DATA: CommissionMonthlyData = { isLocked: false, isUnlockedForEdit: false, rows: [], onlyMine: false };

// Khung giải thích số "Tính vào hoa hồng": số đã thu − thuế VAT − chiết khấu đối tác (chỉ admin / chính sale đó thấy dòng này)
const commissionBreakdown = (record: CommissionRevenueDetail) => {
  const collected = record.amount || 0;
  const base = record.preTaxAmount ?? collected;
  const deducted = Math.max(0, collected - base);
  const money = (value: number) => formatVND(value); // formatVND đã kèm "đ"

  if (deducted <= 0) {
    return <div>Không có thuế hay khoản trừ, tính nguyên số đã thu: {money(base)}</div>;
  }

  const showSplit = record.partnerPart !== undefined;
  return (
    <div style={{ lineHeight: 1.7 }}>
      <div className="font-semibold">Cách tính của đợt thu này</div>
      <div>Số đã thu (gồm VAT): {money(collected)}</div>
      {showSplit ? (
        <>
          {(record.vatPart || 0) > 0 && <div>− Thuế VAT: {money(record.vatPart || 0)}</div>}
          {(record.partnerPart || 0) > 0 && <div>− Chiết khấu đối tác: {money(record.partnerPart || 0)}</div>}
        </>
      ) : (
        <div>− Thuế VAT và các khoản không tính hoa hồng: {money(deducted)}</div>
      )}
      <div className="font-semibold">= Tính vào hoa hồng: {money(base)}</div>
    </div>
  );
};

export const CommissionPage = observer(({ title = "" }) => {
  const [monthValue, setMonthValue] = useState<Dayjs>(dayjs().startOf("month"));
  const [staffId, setStaffId] = useState<number | undefined>();
  const [data, setData] = useState<CommissionMonthlyData>(EMPTY_DATA);
  const [loading, setLoading] = useState(false);

  const [unlockVisible, setUnlockVisible] = useState(false);
  const [unlockNote, setUnlockNote] = useState("");
  const [unlockLoading, setUnlockLoading] = useState(false);

  const [revenueDetailStaff, setRevenueDetailStaff] = useState<CommissionRow | null>(null);
  const [revenueDetailRows, setRevenueDetailRows] = useState<CommissionRevenueDetail[]>([]);
  const [revenueDetailLoading, setRevenueDetailLoading] = useState(false);

  const onlyMine = !userStore.info.role?.isAdmin && can(commissionPath, "ONLY_MINE");
  const canCreate = can(commissionPath, "CREATE");
  const canUnlock = can(commissionPath, "UNLOCK");

  const staffFilter = useRemoteOptions(fetchStaffOptions, !onlyMine);

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await commissionApi.findAll({
        month: monthValue.month() + 1,
        year: monthValue.year(),
        staffId: onlyMine ? undefined : staffId,
      });
      setData({ ...EMPTY_DATA, ...res.data });
    } finally {
      setLoading(false);
    }
  }, [monthValue, staffId, onlyMine]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const { isLocked, rows } = data;
  const paging = useClientPaging(rows);
  const team = rows[0];

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({
          personal: acc.personal + (row.personalCommissionAmount || 0),
          team: acc.team + (row.teamCommissionAmount || 0),
        }),
        { personal: 0, team: 0 },
      ),
    [rows],
  );
  const totalIncome = totals.personal + totals.team;

  const handlePrevMonth = () => setMonthValue((prev) => prev.subtract(1, "month"));
  const handleNextMonth = () => setMonthValue((prev) => prev.add(1, "month"));

  const handleLock = async () => {
    // Chốt hoa hồng luôn áp dụng cho TOÀN BỘ tháng, không theo bộ lọc nhân viên đang xem trên màn hình —
    // nên phải lấy lại số liệu KHÔNG lọc để hiển thị đúng trong hộp thoại xác nhận.
    const res = await commissionApi.findAll({ month: monthValue.month() + 1, year: monthValue.year() });
    const fullRows: CommissionRow[] = res.data.rows || [];

    if (!fullRows.length) {
      message.warning("Chưa có nhân viên nào có cấu hình KPI hoặc doanh thu trong tháng này để chốt");
      return;
    }

    const fullTotalIncome = fullRows.reduce((sum, row) => sum + (row.personalCommissionAmount || 0) + (row.teamCommissionAmount || 0), 0);

    Modal.confirm({
      title: "Xác nhận chốt hoa hồng",
      content: `Chốt hoa hồng tháng ${monthValue.format(
        "MM/YYYY",
      )} cho toàn bộ ${fullRows.length} nhân viên đủ điều kiện (áp dụng cho cả tháng, không theo bộ lọc nhân viên đang xem), tổng chi trả dự kiến ${formatVND(fullTotalIncome)}. Sau khi chốt, số liệu tháng này sẽ được khoá — nếu nhân viên có thay đổi cấu hình sau đó cũng không ảnh hưởng đến bản ghi đã chốt.`,
      okText: "Xác nhận chốt",
      cancelText: "Huỷ",
      onOk: async () => {
        await commissionApi.lock({ month: monthValue.month() + 1, year: monthValue.year() });
        message.success("Chốt hoa hồng thành công");
        fetchData();
      },
    });
  };

  const handleUnlock = async () => {
    if (!unlockNote.trim()) {
      message.error("Vui lòng nhập lý do mở khoá");
      return;
    }
    setUnlockLoading(true);
    try {
      await commissionApi.unlock({
        month: monthValue.month() + 1,
        year: monthValue.year(),
        note: unlockNote.trim(),
      });
      message.success("Mở khoá thành công — số liệu sẽ tự tính lại theo cấu hình nhân viên hiện tại");
      setUnlockVisible(false);
      setUnlockNote("");
      fetchData();
    } finally {
      setUnlockLoading(false);
    }
  };

  const openRevenueDetail = useCallback(
    async (row: CommissionRow) => {
      setRevenueDetailStaff(row);
      setRevenueDetailLoading(true);
      try {
        const res = await commissionApi.findRevenueDetail({
          month: monthValue.month() + 1,
          year: monthValue.year(),
          staffId: row.staffId,
        });
        setRevenueDetailRows(res.data.orderDebts || []);
      } finally {
        setRevenueDetailLoading(false);
      }
    },
    [monthValue],
  );

  const columns = useMemo<TableColumnsType<CommissionRow>>(
    () => [
      {
        title: "Nhân viên",
        dataIndex: "staffName",
        key: "staffName",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Doanh thu đã thu",
        key: "revenueCollected",
        render: (_, row) => (
          <div className="flex items-center gap-1">
            <TableContent>{formatVND(row.revenueCollected)}</TableContent>
            {row.revenueCollected > 0 && (
              <AppButton
                tone="light"
                aria-label="Xem danh sách doanh thu"
                onClick={() => openRevenueDetail(row)}
                style={{ padding: "2px 6px" }}
              >
                <UnorderedListOutlined />
              </AppButton>
            )}
          </div>
        ),
      },
      {
        title: "Mục tiêu (KPI)",
        dataIndex: "targetRevenue",
        key: "targetRevenue",
        render: (value) => <TableContent>{value > 0 ? formatVND(value) : "Chưa cấu hình"}</TableContent>,
      },
      {
        title: "Kết quả",
        key: "kpiResult",
        render: (_, row) =>
          row.targetRevenue > 0 ? (
            <StatusCell value={row.kpiResult} trans={commissionKpiResultTrans} />
          ) : (
            <Tag color="default">Chưa cấu hình KPI</Tag>
          ),
      },
      {
        title: "Hoa hồng cá nhân",
        dataIndex: "personalCommissionAmount",
        key: "personalCommissionAmount",
        render: (value) => <TableContent>{formatVND(value)}</TableContent>,
      },
      {
        title: "Đóng góp đội",
        key: "contribution",
        render: (_, row) => (
          <TableContent>
            {row.targetRevenue > 0 && row.teamRevenueCollected > 0
              ? ((row.revenueCollected / row.teamRevenueCollected) * 100).toFixed(1) + "%"
              : "--"}
          </TableContent>
        ),
      },
      {
        title: "Thưởng đội",
        dataIndex: "teamCommissionAmount",
        key: "teamCommissionAmount",
        render: (value) => <TableContent>{value ? formatVND(value) : "--"}</TableContent>,
      },
      {
        title: "Tổng hoa hồng",
        key: "totalCommission",
        render: (_, row) => (
          <span className="font-semibold">{formatVND((row.personalCommissionAmount || 0) + (row.teamCommissionAmount || 0))}</span>
        ),
      },
    ],
    [openRevenueDetail],
  );

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1">
              <AppButton tone="light" aria-label="Tháng trước" onClick={handlePrevMonth}>
                <LeftOutlined />
              </AppButton>
              <div className="min-w-[110px] text-center font-semibold">{monthValue.format("MM/YYYY")}</div>
              <AppButton tone="light" aria-label="Tháng sau" onClick={handleNextMonth}>
                <RightOutlined />
              </AppButton>
            </div>

            <Tag color={isLocked ? "default" : "orange"}>{isLocked ? "🔒 Đã chốt" : "⚡ Chưa chốt — tính tự động"}</Tag>

            {!onlyMine && (
              <Select
                allowClear
                showSearch
                placeholder="Tất cả nhân viên"
                style={{ width: 220 }}
                value={staffId}
                options={staffFilter.options}
                filterOption={false}
                onSearch={staffFilter.onSearch}
                onChange={(value) => {
                  staffFilter.select(value as number | undefined);
                  setStaffId(value as number | undefined);
                }}
              />
            )}
          </div>

          <div className="flex items-center gap-2">
            {canCreate && !isLocked && (
              <AppButton tone="primary" icon={<LockOutlined />} onClick={handleLock}>
                Chốt hoa hồng
              </AppButton>
            )}
            {canUnlock && isLocked && (
              <AppButton tone="light" icon={<UnlockOutlined />} onClick={() => setUnlockVisible(true)}>
                Mở khoá
              </AppButton>
            )}
          </div>
        </div>
      </Card>

      {onlyMine && (
        <div className="text-xs text-gray-400">
          Bạn chỉ xem được hoa hồng của chính mình, theo danh sách khách hàng đã được gán.
        </div>
      )}

      {team && (
        <Card className="!bg-gradient-to-br !from-[#0f8e91] !to-[#0c7376]" bodyStyle={{ color: "#fff" }}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide opacity-85">
                Hoa hồng đội nhóm · {monthValue.format("MM/YYYY")}
              </div>
              <div className="text-2xl font-extrabold mt-1">
                {formatVND(team.teamRevenueCollected)}{" "}
                <span className="text-sm font-normal opacity-85">
                  / mục tiêu {formatVND(team.teamTargetRevenue)} (
                  {team.teamTargetRevenue > 0 ? ((team.teamRevenueCollected / team.teamTargetRevenue) * 100).toFixed(0) : 0}%)
                </span>
              </div>
            </div>
            <div className="text-right text-sm opacity-90">
              {team.teamRevenueCollected >= team.teamTargetRevenue && team.teamTargetRevenue > 0
                ? `Quỹ thưởng đội: ${formatVND(team.teamRevenueCollected * (team.teamBonusRate / 100))} (${team.teamBonusRate}% doanh thu đội, chia theo tỉ lệ đóng góp)`
                : "Chưa đạt KPI chung — chưa phát sinh thưởng đội"}
            </div>
          </div>
        </Card>
      )}

      <Card title="Tổng hợp tiền hoa hồng">
        <Row gutter={16}>
          <Col span={8}>
            <div className="text-xs text-gray-400 font-semibold uppercase">Hoa hồng cá nhân</div>
            <div className="text-xl font-bold mt-1">{formatVND(totals.personal)}</div>
          </Col>
          <Col span={8}>
            <div className="text-xs text-gray-400 font-semibold uppercase">Hoa hồng đội nhóm</div>
            <div className="text-xl font-bold mt-1">{formatVND(totals.team)}</div>
          </Col>
          <Col span={8}>
            <div className="text-xs text-teal-600 font-semibold uppercase">Tổng hoa hồng</div>
            <div className="text-xl font-extrabold mt-1 text-teal-700">{formatVND(totalIncome)}</div>
          </Col>
        </Row>
      </Card>

      <TableLayout
        table={
          <AppTable<CommissionRow>
            rowKey="staffId"
            loading={loading}
            pagination={false}
            dataSource={paging.pageRows}
            columns={columns}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={paging.showingFrom}
            showingTo={paging.showingTo}
            total={paging.total}
            currentPage={paging.page}
            pageSize={paging.limit}
            onChange={paging.onChange}
          />
        }
      />

      <Modal
        open={unlockVisible}
        title="Mở khoá hoa hồng"
        okText="Xác nhận mở khoá"
        cancelText="Huỷ"
        confirmLoading={unlockLoading}
        onCancel={() => setUnlockVisible(false)}
        onOk={handleUnlock}
      >
        <div className="text-sm text-gray-500 mb-3">
          Mở khoá bản ghi tháng {monthValue.format("MM/YYYY")} để tính lại theo cấu hình nhân viên mới nhất. Hành động
          này sẽ được ghi lại (ai mở khoá, lúc nào, lý do).
        </div>
        <Form layout="vertical">
          <Form.Item label={<>Lý do mở khoá <span className="text-red-500">*</span></>} required>
            <TextArea
              rows={3}
              value={unlockNote}
              onChange={(e) => setUnlockNote(e.target.value)}
              placeholder="VD: Nhân viên vừa đổi mục tiêu KPI, cần tính lại hoa hồng tháng này..."
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={!!revenueDetailStaff}
        title={`Danh sách doanh thu — ${revenueDetailStaff?.staffName || ""} (${monthValue.format("MM/YYYY")})`}
        width={1280}
        footer={null}
        onCancel={() => setRevenueDetailStaff(null)}
      >
        <AppTable<CommissionRevenueDetail>
          rowKey="id"
          loading={revenueDetailLoading}
          pagination={false}
          dataSource={revenueDetailRows}
          columns={[
            {
              title: "Khách hàng",
              key: "customer",
              render: (_, record) => <TableContent>{record.order?.customer?.name || "--"}</TableContent>,
            },
            {
              title: "Mã đơn hàng",
              key: "orderCode",
              width: 130,
              render: (_, record) => <TableContent>{record.order?.code || "--"}</TableContent>,
            },
            {
              title: "Mã báo giá",
              key: "opportunityCode",
              width: 130,
              render: (_, record) => <TableContent>{record.order?.opportunity?.code || "--"}</TableContent>,
            },
            {
              title: "Tên cơ hội",
              key: "opportunityName",
              width: 200,
              render: (_, record) => <TableContent>{record.order?.opportunity?.name || "--"}</TableContent>,
            },
            {
              title: "Nội dung đợt thu",
              dataIndex: "content",
              key: "content",
              width: 150,
              render: (value) => <TableContent>{value || "--"}</TableContent>,
            },
            {
              title: "Ngày thu",
              dataIndex: "collectedDate",
              key: "collectedDate",
              width: 130,
              render: (value) => <TableContent>{value || "--"}</TableContent>,
            },
            {
              title: "Số tiền đã thu (gồm VAT)",
              dataIndex: "amount",
              key: "amount",
              width: 190,
              render: (value) => <TableContent>{formatVND(value)}</TableContent>,
            },
            {
              title: "Tính vào hoa hồng",
              key: "preTaxAmount",
              width: 170,
              render: (_, record) => (
                <Tooltip title={commissionBreakdown(record)} overlayStyle={{ maxWidth: 360 }}>
                  <span className="font-semibold" style={{ cursor: "help", borderBottom: "1px dotted #98a2b3" }}>
                    {formatVND(record.preTaxAmount ?? record.amount)}
                  </span>
                </Tooltip>
              ),
            },
          ]}
        />
        <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100 text-sm">
          <span className="text-gray-400">Tổng cộng</span>
          <span className="font-semibold">
            {formatVND(revenueDetailRows.reduce((sum, row) => sum + (row.preTaxAmount ?? row.amount ?? 0), 0))}
          </span>
        </div>
      </Modal>
    </div>
  );
});
